/**
 * Human-readable formatting helpers for the AGMS CRM UI.
 *
 * Every place that displays a pipeline stage, lead source, sub-stage,
 * or any other snake_case enum to a user should go through these
 * helpers — not raw string interpolation.
 */

import { PIPELINE_STAGES, LEAD_SOURCES } from '../hooks/useLeads'

/**
 * Convert any snake_case or kebab-case string to Title Case.
 *
 * Examples:
 *   'application_entry' → 'Application Entry'
 *   'new_business'      → 'New Business'
 *   'client-returned'   → 'Client Returned'
 *   null / undefined    → ''
 */
export function formatLabel(value: string | null | undefined): string {
  if (!value) return ''
  return value
    .replace(/[_-]/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

/**
 * Format a pipeline stage code to its human-readable label.
 *
 * Uses the canonical PIPELINE_STAGES map first (exact labels from
 * CLAUDE.md Section 8), falls back to formatLabel() for unknown
 * stage codes so the UI never shows raw snake_case to a user.
 */
export function formatStage(stage: string | null | undefined): string {
  if (!stage) return ''
  const config = PIPELINE_STAGES[stage as keyof typeof PIPELINE_STAGES]
  return config?.label ?? formatLabel(stage)
}

/**
 * Format a lead source code to its human-readable label.
 *
 * Uses the canonical LEAD_SOURCES map first, falls back to
 * formatLabel().
 */
export function formatSource(source: string | null | undefined): string {
  if (!source) return ''
  const label = LEAD_SOURCES[source as keyof typeof LEAD_SOURCES]
  return label ?? formatLabel(source)
}

/**
 * Get the Tailwind color family for a pipeline stage.
 *
 * Returns the color string from PIPELINE_STAGES (e.g. 'gray',
 * 'blue', 'amber', 'red', 'emerald'). Used by components that
 * need the stage color but don't use PipelineBadge (e.g. the
 * table row left-border accent, the dashboard stat cards).
 */
export function stageColor(stage: string | null | undefined): string {
  if (!stage) return 'gray'
  const config = PIPELINE_STAGES[stage as keyof typeof PIPELINE_STAGES]
  return config?.color ?? 'gray'
}

/**
 * Map stage color families to CSS border-color values.
 *
 * Used by the leads table row accent and anywhere else that
 * needs a concrete CSS color from a stage. Inline styles because
 * dynamic Tailwind class names (e.g. `border-l-${color}-500`)
 * are purged by the compiler.
 */
export const STAGE_BORDER_COLORS: Record<string, string> = {
  gray: '#9ca3af',
  blue: '#3b82f6',
  green: '#22c55e',
  purple: '#a855f7',
  amber: '#f59e0b',
  red: '#ef4444',
  teal: '#14b8a6',
  emerald: '#10b981',
}

/**
 * Format currency with proper null handling.
 *
 * null → null (let the caller decide how to render nothing)
 * 0    → '$0'
 * 1234 → '$1,234'
 */
export function formatCurrency(value: number | null | undefined): string | null {
  if (value === null || value === undefined) return null
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value)
}

export function formatCurrencyOrDash(value: number | null | undefined): string {
  return formatCurrency(value) ?? '-'
}

export function formatCurrencyWithCentsOrDash(value: number | string | null | undefined): string {
  if (value === null || value === undefined || value === '') return '-'
  const numericValue = typeof value === 'string' ? parseFloat(value) : value
  if (Number.isNaN(numericValue)) return '-'

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(numericValue)
}
